import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hvApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import { Star, Link as LinkIcon, MessageSquare, TrendingUp, Copy, Plus, CheckCircle, ArrowRight, Loader2, Info, Bell, Lock, Lightbulb } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import HVNav from '../components/HVNav';
import HVFooter from '../components/HVFooter';
import FunnelFlow from '../components/FunnelFlow';

// Plan hierarchy: free < standard < premium < enterprise
const PLAN_ORDER = ['free', 'standard', 'premium', 'enterprise'];

const PLAN_FEATURES = [
  { label: 'Bewertungs-Funnel',            min_plan: 'free',       upgrade_label: null },
  { label: '1 Bewertungslink',             min_plan: 'free',       upgrade_label: null,       free_only: true },
  { label: 'Unbegrenzte Links',            min_plan: 'standard',   upgrade_label: 'Standard' },
  { label: 'KI-Textvorschläge',           min_plan: 'standard',   upgrade_label: 'Standard' },
  { label: 'Logo & Markenfarben',         min_plan: 'standard',   upgrade_label: 'Standard' },
  { label: 'Gruppen-Segmentierung',       min_plan: 'standard',   upgrade_label: 'Standard' },
  { label: 'CSV-Export & Funnel-Statistiken', min_plan: 'premium', upgrade_label: 'Premium' },
  { label: 'KI-Sachlichkeitsindex',       min_plan: 'premium',    upgrade_label: 'Premium' },
  { label: 'MetaScore',                   min_plan: 'premium',    upgrade_label: 'Premium' },
  { label: 'Custom Domain',              min_plan: 'enterprise',  upgrade_label: 'Enterprise' },
  { label: 'Custom Design',              min_plan: 'enterprise',  upgrade_label: 'Enterprise' },
  { label: 'Automatische Antworten',     min_plan: 'enterprise',  upgrade_label: 'Enterprise' },
  { label: 'Dedizierter Ansprechpartner', min_plan: 'enterprise', upgrade_label: 'Enterprise' },
];

const PLAN_DISPLAY_NAMES = {
  free: 'Kostenlos',
  mvp: 'Kostenlos',
  standard: 'Standard',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

function planRank(plan) {
  const normalized = plan === 'mvp' ? 'free' : (plan || 'free');
  return PLAN_ORDER.indexOf(normalized);
}

// ─── Entwicklung Chart ────────────────────────────────────────────────────────
const SERIES = [
  {
    key: 'value',
    label: 'MetaScore (Ø)',
    type: 'line',
    color: 'hsl(var(--accent))',
    yAxisId: 'score',
    formatter: (v) => v != null ? `${Number(v).toFixed(1)}/5` : '–',
  },
  {
    key: 'review_count',
    label: 'Neue Bewertungen',
    type: 'bar',
    color: '#6366f1',
    yAxisId: 'count',
    formatter: (v) => v != null ? `${v} Stk.` : '–',
  },
  {
    key: 'positive_rate',
    label: 'Positivrate (%)',
    type: 'line',
    color: '#22c55e',
    yAxisId: 'pct',
    formatter: (v) => v != null ? `${Number(v).toFixed(0)} %` : '–',
  },
  {
    key: 'answer_rate',
    label: 'Antwortrate (%)',
    type: 'line',
    color: '#f59e0b',
    yAxisId: 'pct',
    formatter: (v) => v != null ? `${Number(v).toFixed(0)} %` : '–',
  },
];

const RANGES = [
  { key: '1m', label: '1M' },
  { key: '1y', label: '1J' },
  { key: 'all', label: 'Alle' },
];

function DevelopmentChart() {
  const [range, setRange]   = useState('1y');
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(() => new Set(SERIES.map(s => s.key)));

  useEffect(() => {
    setLoading(true);
    hvApi.getChartData(range)
      .then(r => setData(r.data))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [range]);

  const toggle = (key) => {
    setActive(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  };

  const showPct   = active.has('positive_rate') || active.has('answer_rate');
  const showScore = active.has('value');
  const showCount = active.has('review_count');

  // X-axis format: weekly = "05. Jun", monthly = "Jan 25"
  const tickFmt = (val) => {
    const d = new Date(val);
    if (range === '1m') return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
    return d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
  };

  return (
    <Card className="card-shadow">
      <CardHeader className="pb-3">
        {/* Row 1: title + info + range selector */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">Entwicklung</CardTitle>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-muted-foreground hover:text-foreground transition-colors">
                    <Info className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs text-sm p-3 bg-foreground text-background">
                  <p className="font-semibold mb-1">Über diesen Graphen</p>
                  <ul className="space-y-1 opacity-80">
                    <li><strong>MetaScore</strong> – Ø-Bewertung je Periode</li>
                    <li><strong>Neue Bewertungen</strong> – Eingänge je Periode</li>
                    <li><strong>Positivrate</strong> – Anteil ≥ 4 Sterne</li>
                    <li><strong>Antwortrate</strong> – Anteil beantworteter Bewertungen</li>
                  </ul>
                  <p className="mt-2 opacity-60 text-xs">Checkbox = Linie ein-/ausblenden, um Korrelationen zu erkennen.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {/* Range buttons */}
          <div className="flex items-center rounded-md border overflow-hidden">
            {RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1 text-xs font-medium transition-colors
                  ${range === r.key
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-muted-foreground'}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {/* Row 2: series toggles */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2">
          {SERIES.map(s => (
            <label key={s.key} className="flex items-center gap-1.5 cursor-pointer select-none">
              <input type="checkbox" checked={active.has(s.key)} onChange={() => toggle(s.key)} className="sr-only" />
              <span
                className="w-3 h-3 rounded-sm flex-shrink-0 border transition-colors"
                style={{ backgroundColor: active.has(s.key) ? s.color : 'transparent', borderColor: s.color }}
              />
              <span className={`text-xs ${active.has(s.key) ? 'text-foreground' : 'text-muted-foreground'}`}>
                {s.label}
              </span>
            </label>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : data.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Keine Daten für diesen Zeitraum
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="captured_at" tickFormatter={tickFmt} tick={{ fontSize: 11 }} />
                {showScore && (
                  <YAxis yAxisId="score" domain={[0, 5]} tick={{ fontSize: 11 }} width={28}
                    tickFormatter={(v) => v.toFixed(1)} />
                )}
                {showCount && (
                  <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                )}
                {showPct && !showCount && (
                  <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} width={30}
                    tickFormatter={(v) => `${v}%`} />
                )}
                {showPct && showCount && (
                  <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} hide />
                )}
                <RechartsTooltip
                  formatter={(val, name) => {
                    const s = SERIES.find(s => s.label === name);
                    return [s ? s.formatter(val) : val, name];
                  }}
                  labelFormatter={(val) => new Date(val).toLocaleDateString('de-DE')}
                />
                {SERIES.map(s => {
                  if (!active.has(s.key)) return null;
                  if (s.type === 'bar') {
                    return (
                      <Bar key={s.key} yAxisId={s.yAxisId} dataKey={s.key} name={s.label}
                        fill={s.color} radius={[3, 3, 0, 0]} opacity={0.75} />
                    );
                  }
                  return (
                    <Line key={s.key} yAxisId={s.yAxisId} type="monotone" dataKey={s.key}
                      name={s.label} stroke={s.color} strokeWidth={2}
                      dot={{ r: 3, fill: s.color }} activeDot={{ r: 5 }} />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Next-plan label for locked features — shown as subtle inline text, not a pill
const NEXT_PLAN_COLOR = {
  Standard:   'text-blue-500',
  Premium:    'text-violet-500',
  Enterprise: 'text-amber-500',
};

function PlanCard({ user }) {
  const rawPlan = user?.tenant?.billing?.plan || 'free';
  const normalizedPlan = rawPlan === 'mvp' ? 'free' : rawPlan;
  const planDisplayName = PLAN_DISPLAY_NAMES[rawPlan] || 'Kostenlos';
  const isEnterprise = normalizedPlan === 'enterprise';
  const currentRank = planRank(rawPlan);

  // Count included vs total (excluding free_only helper row for non-free)
  const visibleFeatures = PLAN_FEATURES.filter(f => {
    if (f.free_only && normalizedPlan !== 'free') return false;
    return true;
  });
  const includedCount = visibleFeatures.filter(f => {
    if (f.free_only) return normalizedPlan === 'free';
    return currentRank >= planRank(f.min_plan);
  }).length;
  const totalCount = visibleFeatures.length;
  const pct = Math.round((includedCount / totalCount) * 100);

  return (
    <Card className="card-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Ihr Plan</CardTitle>
          <span className="text-sm font-semibold text-primary">{planDisplayName}</span>
        </div>
        {/* Progress bar: features unlocked */}
        <div className="mt-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-muted-foreground">{includedCount} von {totalCount} Features freigeschaltet</span>
            <span className="text-xs font-medium text-primary">{pct}%</span>
          </div>
          <Progress value={pct} className="h-1.5" />
        </div>
        {!isEnterprise && (
          <Link to="/app/billing" className="text-xs text-primary hover:underline mt-1 inline-block">
            Jetzt upgraden →
          </Link>
        )}
      </CardHeader>
      <CardContent className="pt-2">
        <ul className="space-y-1.5">
          {PLAN_FEATURES.map((feature) => {
            if (feature.free_only && normalizedPlan !== 'free') return null;

            const included = feature.free_only
              ? normalizedPlan === 'free'
              : currentRank >= planRank(feature.min_plan);

            if (included) {
              return (
                <li key={feature.label} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  <span>{feature.label}</span>
                </li>
              );
            }

            const labelColor = NEXT_PLAN_COLOR[feature.upgrade_label] || 'text-muted-foreground/40';
            return (
              <li key={feature.label} className="flex items-center gap-2 text-sm text-muted-foreground/45">
                <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="flex-1">{feature.label}</span>
                {feature.upgrade_label && (
                  <span className={`text-[10px] font-semibold ${labelColor}`}>
                    {feature.upgrade_label}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function HVDashboard() {
  const { user, loading: authLoading } = useAuth('hv_user');
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [links, setLinks] = useState([]);
  const [funnelFlow, setFunnelFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [brandColor, setBrandColor] = useState('#1a56db');
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsRes, linksRes, flowRes] = await Promise.all([
          hvApi.getStats(),
          hvApi.getLinks(),
          hvApi.getFunnelFlow().catch(() => null),
        ]);
        setStats(statsRes.data);
        setLinks(linksRes.data);
        if (flowRes) setFunnelFlow(flowRes.data);
      } catch (err) {
        console.error('Error loading dashboard:', err);
        toast.error('Fehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadData();
      setDisplayName(user?.tenant?.public_profile?.display_name || '');
      setLogoUrl(user?.tenant?.public_profile?.logo_url || '');
      setBrandColor(user?.tenant?.public_profile?.brand_color || '#1a56db');
    }
  }, [user]);

  const onboardingSteps = [
    { id: 'link',      label: 'Ersten Bewertungslink erstellen', done: links.length > 0 },
    { id: 'signature', label: 'Link in E-Mail-Signatur einfügen', done: false },
    { id: 'website',   label: 'Link auf Website platzieren', done: false },
    { id: 'portal',    label: 'Link im Kundenportal teilen', done: false },
  ];
  const completedSteps = onboardingSteps.filter(s => s.done).length;

  // Close dropdowns on outside click
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await hvApi.updateTenant({
        display_name: displayName,
        logo_url: logoUrl || null,
        brand_color: brandColor || null,
      });
      toast.success('Einstellungen gespeichert');
      setSettingsOpen(false);
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingSettings(false);
    }
  };

  const copyLink = (link) => {
    const url = `${window.location.origin}/r/${user?.tenant?.slug}/${link.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link kopiert!');
  };

  const unreadReviews = stats?.unread_reviews ?? 0;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <HVNav unreadReviews={unreadReviews} />

      <main className="container-main py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Bewertungen</p>
                  <p className="text-3xl font-bold" data-testid="kpi-total-reviews">{stats?.total_reviews || 0}</p>
                </div>
                <MessageSquare className="w-5 h-5 text-accent" />
              </div>
              {stats?.recent_reviews > 0 && (
                <Badge variant="secondary" className="mt-2 bg-green-100 text-green-700">
                  +{stats.recent_reviews} diesen Monat
                </Badge>
              )}
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Positive</p>
                  <p className="text-3xl font-bold text-green-600">{stats?.positive_reviews || 0}</p>
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {stats?.total_reviews ? Math.round((stats.positive_reviews / stats.total_reviews) * 100) : 0}% aller Bewertungen
              </p>
            </CardContent>
          </Card>

          {/* MetaScore KPI */}
          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-1 mb-0.5">
                    <p className="text-sm text-muted-foreground">MetaScore</p>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="text-muted-foreground hover:text-foreground transition-colors">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-xs bg-foreground text-background p-3">
                          <p className="font-semibold mb-1">Was ist der MetaScore?</p>
                          <p className="opacity-80 mb-2">Aggregierter Wert aus allen Bewertungsplattformen — Duplikate werden herausgefiltert.</p>
                          <ul className="space-y-0.5 opacity-80">
                            <li>· Immoverita · Google</li>
                            <li>· ProvenExpert · Trustpilot</li>
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-3xl font-bold" data-testid="kpi-metascore-value">
                    {stats?.avg_rating || '-'}
                    <span className="text-lg text-muted-foreground">/5</span>
                  </p>
                </div>
                <Star className="w-5 h-5 text-[hsl(var(--rating-gold))]" />
              </div>
            </CardContent>
          </Card>

          {/* Unread reviews */}
          <Card className={`card-shadow ${unreadReviews > 0 ? 'border-orange-200 bg-orange-50/50' : ''}`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Unbeantwortet</p>
                  <p className={`text-3xl font-bold ${unreadReviews > 0 ? 'text-orange-600' : ''}`}>{unreadReviews}</p>
                </div>
                <Bell className={`w-5 h-5 ${unreadReviews > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
              </div>
              {unreadReviews > 0 && (
                <Link to="/app/reviews?answered=false" className="text-xs text-orange-600 hover:underline mt-2 block">
                  Jetzt ansehen →
                </Link>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Was Immoverita bewirkt hat — Stacked Bar */}
        {stats?.impact && stats.total_reviews > 0 && (() => {
          const total = stats.total_reviews;
          const pos = stats.positive_reviews || 0;
          const neg = total - pos;
          const posPct = Math.round((pos / total) * 100);
          const negPct = 100 - posPct;
          // „Verbessert" = tatsächlich besser geurteilt als beim Impuls (aus funnel-flow),
          // nicht mehr „positive Reviews mit Kategorien" (das waren praktisch alle).
          const impactStats = [
            { value: funnelFlow?.improved ?? stats.impact.convinced_up, label: 'Verbessert',    color: 'text-green-700' },
            { value: stats.impact.with_categories,      label: 'Differenziert', color: 'text-blue-700' },
            { value: funnelFlow?.external_clicks ?? stats.impact.extern_count, label: 'Zu Portalen', color: 'text-violet-700' },
            { value: stats.impact.critical_intercepted, label: 'Abgefangen',    color: 'text-orange-700' },
          ];
          return (
            <div className="mb-8">
              <Card className="card-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Was Immoverita bewirkt hat</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">Messbare Auswirkungen des differenzierten Bewertungsprozesses</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Stacked Bar */}
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                      <span className="font-medium text-green-700">{pos} Positiv ({posPct}%)</span>
                      <span className="font-medium text-red-700">{neg} Kritisch ({negPct}%)</span>
                    </div>
                    <div className="h-5 rounded-full overflow-hidden flex gap-0.5">
                      {pos > 0 && <div className="bg-green-500 rounded-l-full transition-all" style={{ width: `${posPct}%` }} />}
                      {neg > 0 && <div className="bg-red-400 rounded-r-full flex-1 transition-all" />}
                    </div>
                    {(funnelFlow?.converted_up ?? 0) > 0 && (
                      <p className="text-[11px] text-green-600 mt-1.5 font-medium">
                        ↑ {funnelFlow.converted_up} kritische Impuls-{funnelFlow.converted_up === 1 ? 'Bewertung' : 'Bewertungen'} wurden nach der Detailabfrage positiv
                      </p>
                    )}
                  </div>
                  {/* Compact stats row */}
                  <div className="grid grid-cols-4 gap-2">
                    {impactStats.map(({ value, label, color }) => (
                      <div key={label} className="text-center p-2 rounded-lg bg-muted/50">
                        <p className={`text-xl font-bold ${color}`}>{value}</p>
                        <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* Funnel-Klickfluss */}
        {funnelFlow && funnelFlow.started > 0 && (
          <div className="mb-8">
            <Card className="card-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Funnel-Klickfluss (alle Links)</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Wie Eigentümer &amp; Mieter den Prozess durchlaufen — inklusive Verfeinerungs-Schritt, aggregiert über alle Bewertungslinks
                </p>
              </CardHeader>
              <CardContent>
                <FunnelFlow flow={funnelFlow} />
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Entwicklung Chart */}
            <DevelopmentChart />

            {/* Top 3 Links */}
            <Card className="card-shadow">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Ihre Top&nbsp;3 Bewertungslinks</CardTitle>
                <Link to="/app/links">
                  <Button variant="ghost" size="sm">
                    Alle anzeigen <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {links.length === 0 ? (
                  <div className="text-center py-8">
                    <LinkIcon className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-muted-foreground mb-4">Noch keine Links erstellt</p>
                    <Link to="/app/links">
                      <Button data-testid="hv-create-first-link-button">
                        <Plus className="w-4 h-4 mr-2" />
                        Ersten Link erstellen
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {[...links]
                      .sort((a, b) => (b.review_count || 0) - (a.review_count || 0))
                      .slice(0, 3)
                      .map((link, idx) => {
                        const url = `${window.location.origin}/r/${user?.tenant?.slug}/${link.slug}`;
                        return (
                          <div key={link.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                            {/* Rank number */}
                            <span className="text-xs font-bold text-muted-foreground/50 w-4 shrink-0 text-center">
                              {idx + 1}
                            </span>
                            {/* Name + URL */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <Link
                                  to={`/app/links/${link.id}`}
                                  className="font-medium text-sm hover:text-primary hover:underline truncate"
                                >
                                  {link.name}
                                </Link>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-5 w-5 shrink-0"
                                  onClick={() => copyLink(link)}
                                  data-testid={`links-table-row-${link.id}-copy-button`}
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                              <span className="text-xs text-muted-foreground truncate block">{url}</span>
                            </div>
                            {/* Review count */}
                            <div className="shrink-0 text-right">
                              <span className="text-sm font-semibold">{link.review_count || 0}</span>
                              <p className="text-[10px] text-muted-foreground leading-none">Bewertungen</p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Onboarding Checklist */}
            <Card className="card-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Onboarding</CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Progress value={(completedSteps / onboardingSteps.length) * 100} className="flex-1 h-1.5" />
                  <span className="text-xs text-muted-foreground shrink-0">{completedSteps}/{onboardingSteps.length}</span>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-2.5">
                  {onboardingSteps.map((step) => (
                    <li key={step.id} className="flex items-center gap-2.5">
                      {step.done
                        ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        : <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                      }
                      <span className={`text-sm ${step.done ? 'text-muted-foreground line-through' : ''}`}>
                        {step.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Plan Card */}
            <PlanCard user={user} />

          </div>
        </div>

        {/* Settings Dialog */}
        {/* ── Immoverita Tipp ──────────────────────────────────────── */}
        <div className="mt-8 rounded-xl shadow-sm p-5 flex items-start gap-4"
             style={{
               background: 'linear-gradient(135deg, #f0fdf8 0%, #eff6ff 100%) padding-box, linear-gradient(135deg, rgba(16,185,129,0.3), rgba(59,130,246,0.3)) border-box',
               border: '2px solid transparent',
             }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
               style={{ background: 'linear-gradient(135deg, #3b82f6, #10b981)' }}>
            <Lightbulb className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400 mb-1 block">Immoverita Tipp</span>
            <p className="text-sm font-medium text-slate-700 mb-3 leading-snug">
              Unser Bewertungs-Funnel leitet zufriedene Bewerter automatisch auf Google, ProvenExpert oder Trustpilot — während Sie bei unzufriedenen Bewertern die Möglichkeit haben zu reagieren.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 text-xs">
              {[
                { step: '1', title: 'Bewerter im Immoverita-Funnel', desc: 'Der Bewerter klickt auf 1–3 Sterne → landet im Immoverita Funnel und ist nicht direkt öffentlich einsehbar.' },
                { step: '2', title: 'Sie reagieren zuerst', desc: 'Sie sehen die Kritik in Ihrem Dashboard und können direkt darauf reagieren — bevor es öffentlich wird.' },
                { step: '3', title: 'Zufriedene Bewerter gehen weiter', desc: 'Zufriedene Bewerter klicken auf öffentliche Plattformen weiter.' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-2.5 p-3 rounded-lg bg-white/60 border border-slate-100">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0 mt-0.5"
                        style={{ background: 'linear-gradient(135deg, #3b82f6, #10b981)' }}>
                    {step}
                  </span>
                  <div>
                    <p className="font-semibold text-slate-600 mb-0.5">{title}</p>
                    <p className="text-slate-400 leading-snug">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Unternehmenseinstellungen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Anzeigename</label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Name Ihrer Hausverwaltung"
                />
                <p className="text-xs text-muted-foreground mt-1">Wird Bewertenden im Funnel-Header angezeigt.</p>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Logo URL</label>
                <Input
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://beispiel.de/logo.png"
                />
                <p className="text-xs text-muted-foreground mt-1">Wird im Bewertungs-Funnel als Logo angezeigt.</p>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Markenfarbe</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-border"
                  />
                  <Input
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    placeholder="#1a56db"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">E-Mail</label>
                <Input defaultValue={user?.email || ''} disabled />
                <p className="text-xs text-muted-foreground mt-1">E-Mail kann nicht geändert werden.</p>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Plan</label>
                <Badge variant="outline">{user?.tenant?.billing?.plan?.toUpperCase() || 'FREE'}</Badge>
                <span className="text-xs text-muted-foreground ml-2">
                  {user?.tenant?.billing?.payment_status || 'Trial'}
                </span>
              </div>
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Für Änderungen an Billing oder Plan: af@dwayne-advertising.de
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setSettingsOpen(false)}>Abbrechen</Button>
              <Button onClick={handleSaveSettings} disabled={savingSettings}>
                {savingSettings ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : 'Speichern'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      <HVFooter />
      </main>
    </div>
  );
}
